import { MessageSquare, FileText, Database, GitBranch, BookOpen, Settings, Bell, User, LogOut, Coins, ChevronDown, ListTodo, MoreVertical, Folder, MessageCircle, Info, HelpCircle, FileEdit, Lock } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCompactMode } from '@/hooks/useCompactMode';
import { usePremium } from '@/hooks/usePremium';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { CreditsModal } from './CreditsModal';
import { SettingsModal } from './SettingsModal';
import { FeedbackModal } from './FeedbackModal';
import { AboutModal } from './AboutModal';
import { UserGuide } from './UserGuide';
import { NotificationDropdown } from './NotificationDropdown';
import { useNotificationStore } from '@/stores';
import { creditApi } from '@/services/api';
import logoIcon from '@/assets/loop-icon.png';

interface HeaderProps {
  activePage: 'chat' | 'documents' | 'uspto_data' | 'examination' | 'resources' | 'tasks' | 'sessions' | 'drafts';
  onPageChange: (page: 'chat' | 'documents' | 'uspto_data' | 'examination' | 'resources' | 'tasks' | 'drafts') => void;
}

export function Header({ activePage, onPageChange }: HeaderProps) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { compactMode: globalCompactMode } = useCompactMode();
  const { isPremium } = usePremium();
  const { addNotification } = useNotificationStore();
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [credits, setCredits] = useState<number | null>(null);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [isCompactMode, setIsCompactMode] = useState(false);
  const [isCompactNav, setIsCompactNav] = useState(false);

  const notifications = useNotificationStore(state => state.notifications);

  // Count only unread success notifications for the bell badge
  const unreadSuccessCount = notifications.filter(n => n.type === 'success' && !n.read).length;

  // Fetch credits
  const fetchCredits = async () => {
    try {
      const response = await creditApi.getCreditBalance();
      if (response.success && response.remaining_credits !== undefined) {
        setCredits(response.remaining_credits);
      }
    } catch (error) {
      console.error('Failed to fetch credits:', error);
      // Don't show error to user, just silently fail
    } finally {
      setCreditsLoading(false);
    }
  };

  // Fetch credits on mount and listen for updates
  useEffect(() => {
    fetchCredits();

    // Listen for credit updates (triggered after chat messages, etc.)
    const handleCreditsUpdate = () => {
      fetchCredits();
    };

    window.addEventListener('credits-updated', handleCreditsUpdate);

    return () => {
      window.removeEventListener('credits-updated', handleCreditsUpdate);
    };
  }, []);

  // Track window width for compact modes
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsCompactMode(width < 1350);
      setIsCompactNav(width < 1150);
    };

    // Initial check
    handleResize();

    // Listen for resize events
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Helper to get page display info
  const getPageInfo = (page: typeof activePage) => {
    switch (page) {
      case 'chat':
        return { icon: MessageSquare, label: 'Chat' };
      case 'documents':
        return { icon: FileText, label: 'Documents' };
      case 'uspto_data':
        return { icon: Database, label: 'USPTO Data' };
      case 'examination':
        return { icon: GitBranch, label: 'Examination' };
      case 'resources':
        return { icon: BookOpen, label: 'Resources' };
      case 'tasks':
        return { icon: ListTodo, label: 'Tasks' };
      case 'sessions':
        return { icon: Folder, label: 'Sessions' };
      default:
        return { icon: MessageSquare, label: 'Chat' };
    }
  };

  const currentPageInfo = getPageInfo(activePage);
  const CurrentPageIcon = currentPageInfo.icon;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleOpenSettings = () => {
    // Small delay to ensure dropdown menu overlay is removed before opening modal
    setTimeout(() => {
      setShowSettingsModal(true);
    }, 100);
  };

  const handleOpenFeedback = () => {
    // Small delay to ensure dropdown menu overlay is removed before opening modal
    setTimeout(() => {
      setShowFeedbackModal(true);
    }, 100);
  };

  const handleOpenAbout = () => {
    // Small delay to ensure dropdown menu overlay is removed before opening modal
    setTimeout(() => {
      setShowAboutModal(true);
    }, 100);
  };

  const handleOpenUserGuide = () => {
    // Small delay to ensure dropdown menu overlay is removed before opening modal
    setTimeout(() => {
      setShowUserGuide(true);
    }, 100);
  };

  const handleDraftsClick = () => {
    if (!isPremium) {
      addNotification({
        type: 'warning',
        title: 'Premium Feature',
        message: 'Drafts is a premium-only feature. Please upgrade your account to access this functionality.',
        duration: 5000
      });
      return;
    }
    onPageChange('drafts');
  };

  return (
    <header className={`border-b border-gray-200 bg-white ${globalCompactMode ? 'px-3 py-1' : 'px-6 py-2'}`}>
      <div className={`flex items-center justify-between ${globalCompactMode ? 'gap-2' : ''}`}>
        <div className={`flex items-center ${globalCompactMode ? 'gap-2' : 'gap-4'}`}>
          <div className="flex items-center gap-2">
            <img
              src={logoIcon}
              alt="Patent Loop"
              className={`${globalCompactMode ? 'h-6 w-auto' : 'h-7 w-auto'} cursor-pointer hover:opacity-80 transition-opacity`}
              title="Click to open User Guide"
              onClick={handleOpenUserGuide}
            />
            <span className="px-2 py-0.5 text-[10px] font-bold bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-md shadow-sm">
              BETA
            </span>
          </div>

          {/* Full Navigation (>= 1150px) */}
          {!isCompactNav && (
            <nav className="flex items-center gap-1">
              <Button
                variant={activePage === 'chat' ? 'secondary' : 'ghost'}
                size={globalCompactMode ? 'sm' : 'default'}
                onClick={() => onPageChange('chat')}
                className={globalCompactMode ? 'gap-1 text-xs px-2' : 'gap-2'}
              >
                <MessageSquare className={globalCompactMode ? 'w-3 h-3' : 'w-4 h-4'} />
                Chat
              </Button>
              <Button
                variant={activePage === 'documents' ? 'secondary' : 'ghost'}
                size={globalCompactMode ? 'sm' : 'default'}
                onClick={() => onPageChange('documents')}
                className={globalCompactMode ? 'gap-1 text-xs px-2' : 'gap-2'}
              >
                <FileText className={globalCompactMode ? 'w-3 h-3' : 'w-4 h-4'} />
                Documents
              </Button>
              <Button
                variant={activePage === 'uspto_data' ? 'secondary' : 'ghost'}
                size={globalCompactMode ? 'sm' : 'default'}
                onClick={() => onPageChange('uspto_data')}
                className={globalCompactMode ? 'gap-1 text-xs px-2' : 'gap-2'}
              >
                <Database className={globalCompactMode ? 'w-3 h-3' : 'w-4 h-4'} />
                USPTO Data
              </Button>
              <Button
                variant={activePage === 'examination' ? 'secondary' : 'ghost'}
                size={globalCompactMode ? 'sm' : 'default'}
                onClick={() => onPageChange('examination')}
                className={globalCompactMode ? 'gap-1 text-xs px-2' : 'gap-2'}
              >
                <GitBranch className={globalCompactMode ? 'w-3 h-3' : 'w-4 h-4'} />
                Examination
              </Button>
              <Button
                variant={activePage === 'resources' ? 'secondary' : 'ghost'}
                size={globalCompactMode ? 'sm' : 'default'}
                onClick={() => onPageChange('resources')}
                className={globalCompactMode ? 'gap-1 text-xs px-2' : 'gap-2'}
              >
                <BookOpen className={globalCompactMode ? 'w-3 h-3' : 'w-4 h-4'} />
                Resources
              </Button>
              <Button
                variant={activePage === 'tasks' ? 'secondary' : 'ghost'}
                size={globalCompactMode ? 'sm' : 'default'}
                onClick={() => onPageChange('tasks')}
                className={globalCompactMode ? 'gap-1 text-xs px-2' : 'gap-2'}
              >
                <ListTodo className={globalCompactMode ? 'w-3 h-3' : 'w-4 h-4'} />
                Tasks
              </Button>
            </nav>
          )}

          {/* Compact Navigation Selector (< 1150px) */}
          {isCompactNav && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" className="gap-2">
                  <CurrentPageIcon className="w-4 h-4" />
                  {currentPageInfo.label}
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-white border border-gray-200 shadow-lg">
                <DropdownMenuItem
                  onClick={() => onPageChange('chat')}
                  className={`text-gray-700 hover:bg-gray-100 cursor-pointer ${activePage === 'chat' ? 'bg-blue-50' : ''}`}
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Chat
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onPageChange('documents')}
                  className={`text-gray-700 hover:bg-gray-100 cursor-pointer ${activePage === 'documents' ? 'bg-blue-50' : ''}`}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Documents
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onPageChange('uspto_data')}
                  className={`text-gray-700 hover:bg-gray-100 cursor-pointer ${activePage === 'uspto_data' ? 'bg-blue-50' : ''}`}
                >
                  <Database className="w-4 h-4 mr-2" />
                  USPTO Data
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onPageChange('examination')}
                  className={`text-gray-700 hover:bg-gray-100 cursor-pointer ${activePage === 'examination' ? 'bg-blue-50' : ''}`}
                >
                  <GitBranch className="w-4 h-4 mr-2" />
                  Examination
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onPageChange('resources')}
                  className={`text-gray-700 hover:bg-gray-100 cursor-pointer ${activePage === 'resources' ? 'bg-blue-50' : ''}`}
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Resources
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onPageChange('tasks')}
                  className={`text-gray-700 hover:bg-gray-100 cursor-pointer ${activePage === 'tasks' ? 'bg-blue-50' : ''}`}
                >
                  <ListTodo className="w-4 h-4 mr-2" />
                  Tasks
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Desktop View (>= 1350px) - Show all items separately */}
          {!isCompactMode && (
            <>
              <Badge
                variant="outline"
                className="gap-1 bg-blue-50 text-blue-700 border-blue-200 cursor-pointer hover:bg-blue-100 hover:border-blue-300 transition-colors"
                onClick={() => setShowCreditsModal(true)}
                title="Click to view credit details"
              >
                <Coins className="w-3 h-3" />
                {creditsLoading ? (
                  'Loading...'
                ) : credits !== null ? (
                  `${credits.toLocaleString()} credits`
                ) : (
                  'Credits unavailable'
                )}
              </Badge>

              <Button
                variant="ghost"
                size="icon"
                onClick={handleDraftsClick}
                title={isPremium ? "Drafts" : "Drafts (Premium Feature)"}
              >
                <FileEdit className="w-5 h-5" />
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative">
                    <Bell className="w-5 h-5" />
                    {unreadSuccessCount > 0 && (
                      <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                        {unreadSuccessCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="p-0">
                  <NotificationDropdown />
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <User className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white border border-gray-200 shadow-lg">
                  <DropdownMenuLabel className="text-gray-900">
                    {user?.username || 'My Account'}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-gray-200" />
                  <DropdownMenuItem onClick={handleOpenSettings} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <User className="w-4 h-4 mr-2" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleOpenFeedback} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Send Feedback
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleOpenUserGuide} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <HelpCircle className="w-4 h-4 mr-2" />
                    User Guide
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleOpenAbout} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <Info className="w-4 h-4 mr-2" />
                    About
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-gray-200" />
                  <DropdownMenuItem onClick={handleLogout} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <LogOut className="w-4 h-4 mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}

          {/* Compact View (< 1350px) - Three-dot menu */}
          {isCompactMode && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleDraftsClick}
                title={isPremium ? "Drafts" : "Drafts (Premium Feature)"}
              >
                <FileEdit className="w-5 h-5" />
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreVertical className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white border border-gray-200 shadow-lg w-56">
                <DropdownMenuLabel className="text-gray-900">
                  {user?.username || 'My Account'}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-gray-200" />

                {/* Credits Display */}
                <div className="px-2 py-2">
                  <div
                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 cursor-pointer transition-colors"
                    onClick={() => setShowCreditsModal(true)}
                  >
                    <Coins className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-gray-700">
                      {creditsLoading ? (
                        'Loading...'
                      ) : credits !== null ? (
                        `${credits.toLocaleString()} credits`
                      ) : (
                        'Credits unavailable'
                      )}
                    </span>
                  </div>
                </div>

                <DropdownMenuSeparator className="bg-gray-200" />

                {/* Notifications - Show in compact menu as text, actual dropdown is separate */}
                <div className="px-2 py-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <div className="flex items-center justify-between w-full px-2 py-1.5 rounded hover:bg-gray-100 cursor-pointer transition-colors">
                        <div className="flex items-center">
                          <Bell className="w-4 h-4 mr-2 text-gray-700" />
                          <span className="text-sm text-gray-700">Notifications</span>
                        </div>
                        {unreadSuccessCount > 0 && (
                          <span className="min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                            {unreadSuccessCount}
                          </span>
                        )}
                      </div>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="p-0">
                      <NotificationDropdown />
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <DropdownMenuSeparator className="bg-gray-200" />

                {/* Account Options */}
                <DropdownMenuItem onClick={handleOpenSettings} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/profile')} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleOpenFeedback} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Send Feedback
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleOpenUserGuide} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                  <HelpCircle className="w-4 h-4 mr-2" />
                  User Guide
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleOpenAbout} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                  <Info className="w-4 h-4 mr-2" />
                  About
                </DropdownMenuItem>

                <DropdownMenuSeparator className="bg-gray-200" />

                <DropdownMenuItem onClick={handleLogout} className="text-gray-700 hover:bg-gray-100 cursor-pointer">
                  <LogOut className="w-4 h-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </>
          )}
        </div>
      </div>

      {/* Credits Modal */}
      <CreditsModal
        open={showCreditsModal}
        onOpenChange={setShowCreditsModal}
      />

      {/* Settings Modal */}
      <SettingsModal
        open={showSettingsModal}
        onOpenChange={setShowSettingsModal}
      />

      {/* Feedback Modal */}
      <FeedbackModal
        open={showFeedbackModal}
        onOpenChange={setShowFeedbackModal}
      />

      {/* About Modal */}
      <AboutModal
        open={showAboutModal}
        onOpenChange={setShowAboutModal}
      />

      {/* User Guide */}
      <UserGuide
        open={showUserGuide}
        onOpenChange={setShowUserGuide}
      />
    </header>
  );
}
